import type { GameCourseItem } from "@zrt-ai-office/types/course"

/** AI Agent 闯关课程 Mock 数据 */
export const GAME_COURSES: GameCourseItem[] = [
  {
    id: 9001,
    seriesId: 0,
    title: "AI Agent 从入门到实战",
    intro: "带上你的小龙虾，一步步掌握 AI Agent 的核心能力",
    description:
      "这是一门游戏化的互动课程，你将学习 AI Agent 的基本概念、Prompt 工程、工具调用、多步推理和记忆管理，最终亲手构建一个完整的 Agent。每个关卡都需要你引导你的 AI 助手（小龙虾）完成挑战。",
    coverImage: "",
    courseType: "game",
    sortOrder: 0,
    status: 1,
    createdAt: "2026-03-25T00:00:00Z",
    chapters: [
      /* ── 关卡 1：认识 AI Agent ── */
      {
        id: 8001,
        courseId: 9001,
        title: "认识 AI Agent",
        intro: "了解什么是 AI Agent，它和普通聊天机器人有什么区别",
        objective: "让小龙虾介绍自己是什么类型的 Agent",
        sortOrder: 1,
        status: "available",
        stars: 0,
        steps: [
          {
            id: 7001,
            chapterId: 8001,
            title: "什么是 AI Agent？",
            contentType: "md",
            content: `# 什么是 AI Agent？

**AI Agent**（AI 智能体）是一种能够自主感知环境、做出决策并执行行动的 AI 系统。

与传统的聊天机器人不同，Agent 具备以下关键特征：

- **自主性** — 能够独立规划和执行任务
- **工具使用** — 可以调用外部工具（搜索、代码执行、API 调用等）
- **记忆能力** — 能记住上下文和历史信息
- **多步推理** — 能将复杂任务分解为多个步骤

> 💡 你可以把 Agent 想象成一个拥有"手脚"的 AI —— 它不仅能思考，还能行动。`,
            sortOrder: 1,
          },
          {
            id: 7002,
            chapterId: 8001,
            title: "Agent 的核心组件",
            contentType: "md",
            content: `# Agent 的核心组件

一个典型的 AI Agent 由以下部分组成：

### 🧠 大脑（LLM）
负责理解指令、推理和决策的核心模型。

### 🔧 工具（Tools）
Agent 可以调用的外部能力，如：
- 代码执行器
- 网络搜索
- 文件读写
- API 调用

### 💾 记忆（Memory）
- **短期记忆**：当前对话的上下文
- **长期记忆**：跨会话保存的知识

### 📋 规划器（Planner）
将复杂任务拆解为可执行的步骤序列。`,
            sortOrder: 2,
          },
          {
            id: 7003,
            chapterId: 8001,
            title: "认识你的小龙虾",
            contentType: "md",
            content: `# 认识你的小龙虾 🦞

**小龙虾**是你的专属 AI 助手，它是一个功能完备的 Agent。

小龙虾具备以下能力：
- ✅ 理解自然语言指令
- ✅ 编写和执行代码
- ✅ 搜索和整理信息
- ✅ 多步骤任务规划
- ✅ 学习和记忆

在接下来的关卡中，你需要通过对话来引导小龙虾完成各种挑战。

> 🎯 准备好了吗？点击「下一步」进入你的第一个挑战！`,
            sortOrder: 3,
          },
          {
            id: 7004,
            chapterId: 8001,
            title: "闯关挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾做一个自我介绍，说明它是什么类型的 Agent，具备哪些能力。",
              initialMessage:
                "你好！我是小龙虾 🦞，你的专属 AI 助手。有什么需要我帮忙的吗？",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content:
                    "好的，让我来介绍一下自己！",
                  delay: 800,
                  actionStatus: "thinking",
                  actionLabel: "正在组织语言...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content: `我是**小龙虾**，一个多功能 AI Agent 🦞

我的核心能力包括：

1. **自然语言理解** — 我能准确理解你的指令
2. **代码编写与执行** — 我可以写代码并运行它
3. **信息检索** — 我能搜索和整理各类资料
4. **任务规划** — 我能把复杂任务拆成小步骤
5. **上下文记忆** — 我能记住我们之前聊过的内容

简单来说，我不只是一个聊天机器人——我是一个能**思考**又能**行动**的 AI 智能体！`,
                  delay: 2000,
                  actionStatus: "done",
                  actionLabel: "自我介绍完成",
                },
                {
                  id: "m3",
                  role: "judge",
                  content:
                    "✅ **闯关成功！**\n\n小龙虾成功介绍了自己是一个 AI Agent，并列举了它的核心能力。干得漂亮！",
                  delay: 1500,
                },
              ],
            },
            sortOrder: 4,
          },
        ],
      },

      /* ── 关卡 2：Prompt 工程基础 ── */
      {
        id: 8002,
        courseId: 9001,
        title: "Prompt 工程基础",
        intro: "学习如何编写高质量的 Prompt 来引导 AI",
        objective: "让小龙虾按照特定格式完成一个任务",
        sortOrder: 2,
        status: "locked",
        stars: 0,
        steps: [
          {
            id: 7010,
            chapterId: 8002,
            title: "什么是 Prompt？",
            contentType: "md",
            content: `# 什么是 Prompt？

**Prompt**（提示词）是你给 AI 的指令或输入。Prompt 的质量直接决定了 AI 输出的质量。

好的 Prompt 具备以下特征：

### 1. 清晰具体
❌ "帮我写个东西"
✅ "帮我写一封给客户的感谢邮件，语气要正式但友好"

### 2. 提供上下文
❌ "翻译这个"
✅ "将以下技术文档从英文翻译成中文，保留专业术语"

### 3. 指定输出格式
❌ "列一些建议"
✅ "以 Markdown 列表的形式给出 5 条建议，每条不超过 20 个字"`,
            sortOrder: 1,
          },
          {
            id: 7011,
            chapterId: 8002,
            title: "Prompt 设计技巧",
            contentType: "md",
            content: `# Prompt 设计技巧

### 🎭 角色扮演
给 AI 分配一个角色，可以显著提升输出质量：
> "你是一位资深的 Python 开发工程师，请..."

### 📝 少样本学习（Few-shot）
提供几个示例，让 AI 理解你期望的模式：
> "按照以下格式输出：\n输入：苹果 → 输出：🍎 水果\n输入：汽车 → 输出：？"

### 🔗 思维链（Chain of Thought）
要求 AI 逐步思考：
> "请一步一步分析这个问题，先列出已知条件，再推导结论"

### 📐 约束条件
明确限制和要求：
> "回答不超过 100 字，使用简体中文，避免使用专业术语"`,
            sortOrder: 2,
          },
          {
            id: 7012,
            chapterId: 8002,
            title: "闯关挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾扮演一个 Python 老师的角色，用 3 个步骤解释什么是变量，并给出一个代码示例。",
              initialMessage:
                "我准备好了！你想让我做什么呢？",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content: "收到！让我切换到 Python 老师模式 🎓",
                  delay: 600,
                  actionStatus: "thinking",
                  actionLabel: "正在进入角色...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content: `好的，同学你好！我是你的 Python 老师 🐍

让我用 **3 个步骤**来解释什么是变量：

**第 1 步：理解概念**
变量就像一个"盒子"，你可以在里面放东西（数据），并且给这个盒子起一个名字。

**第 2 步：声明变量**
在 Python 中，声明变量非常简单——直接用 \`=\` 赋值即可，不需要指定类型。

**第 3 步：使用变量**
通过变量名就可以访问盒子里的内容。

下面是一个代码示例：

\`\`\`python
# 声明变量
name = "小龙虾"
age = 1
skills = ["编程", "搜索", "规划"]

# 使用变量
print(f"我叫{name}，今年{age}岁")
print(f"我会的技能：{', '.join(skills)}")
\`\`\`

运行结果：
\`\`\`
我叫小龙虾，今年1岁
我会的技能：编程、搜索、规划
\`\`\``,
                  delay: 2500,
                  actionStatus: "done",
                  actionLabel: "教学内容已生成",
                },
                {
                  id: "m3",
                  role: "judge",
                  content:
                    "✅ **闯关成功！**\n\n小龙虾成功扮演了 Python 老师角色，用 3 个步骤清晰解释了变量概念，并提供了完整的代码示例。你的 Prompt 引导非常棒！",
                  delay: 1500,
                },
              ],
            },
            sortOrder: 3,
          },
        ],
      },

      /* ── 关卡 3：工具调用 ── */
      {
        id: 8003,
        courseId: 9001,
        title: "工具调用（Tool Use）",
        intro: "让 Agent 使用外部工具完成任务",
        objective: "让小龙虾调用工具来解决实际问题",
        sortOrder: 3,
        status: "locked",
        stars: 0,
        steps: [
          {
            id: 7020,
            chapterId: 8003,
            title: "为什么需要工具？",
            contentType: "md",
            content: `# 为什么 Agent 需要工具？

LLM 本身有一些天然的局限：

| 局限 | 工具解决方案 |
|------|------------|
| 不擅长精确计算 | 🔧 代码执行器 / 计算器 |
| 知识有截止日期 | 🔧 网络搜索 |
| 无法访问私有数据 | 🔧 数据库查询 / API 调用 |
| 不能操作外部系统 | 🔧 系统命令 / API |

通过工具调用，Agent 可以突破这些限制，完成更复杂的真实世界任务。

> 🔑 工具调用是 Agent 从"只会说"到"能做事"的关键能力。`,
            sortOrder: 1,
          },
          {
            id: 7021,
            chapterId: 8003,
            title: "工具调用的工作流程",
            contentType: "md",
            content: `# 工具调用的工作流程

Agent 使用工具时遵循一个标准流程：

### 1️⃣ 理解任务
Agent 分析用户的指令，判断是否需要调用工具。

### 2️⃣ 选择工具
从可用工具列表中选择最合适的工具。

### 3️⃣ 构造参数
根据工具的输入规范，准备调用参数。

### 4️⃣ 执行调用
调用工具并等待返回结果。

### 5️⃣ 处理结果
将工具返回的结果整合到最终回答中。

\`\`\`
用户指令 → 选择工具 → 构造参数 → 执行 → 处理结果 → 回答用户
\`\`\`

一个 Agent 可以在一次任务中**连续调用多个工具**，形成工具链。`,
            sortOrder: 2,
          },
          {
            id: 7022,
            chapterId: 8003,
            title: "闯关挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾使用代码执行工具，计算斐波那契数列的第 20 项是多少。",
              initialMessage:
                "我已经准备好了各种工具 🔧，需要我做什么计算吗？",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content:
                    "明白了！计算斐波那契数列第 20 项，让我调用代码执行工具。",
                  delay: 600,
                  actionStatus: "thinking",
                  actionLabel: "正在选择工具...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content:
                    "📦 **调用工具：** `code_executor`\n\n```python\ndef fibonacci(n):\n    a, b = 0, 1\n    for _ in range(n):\n        a, b = b, a + b\n    return a\n\nresult = fibonacci(20)\nprint(f\"斐波那契数列第 20 项是：{result}\")\n```",
                  delay: 1500,
                  actionStatus: "executing",
                  actionLabel: "正在执行代码...",
                },
                {
                  id: "m3",
                  role: "assistant",
                  content:
                    "🎉 执行完成！\n\n**结果：** 斐波那契数列第 20 项是 **6765**\n\n我使用了代码执行器工具，编写了一个迭代算法来计算结果。这比让我心算可靠多了！",
                  delay: 2000,
                  actionStatus: "done",
                  actionLabel: "代码执行完毕",
                },
                {
                  id: "m4",
                  role: "judge",
                  content:
                    "✅ **闯关成功！**\n\n小龙虾正确使用了代码执行工具，编写了斐波那契算法并得到了正确答案 6765。工具调用流程完整！",
                  delay: 1500,
                },
              ],
            },
            sortOrder: 3,
          },
        ],
      },

      /* ── 关卡 4：多步推理与规划 ── */
      {
        id: 8004,
        courseId: 9001,
        title: "多步推理与规划",
        intro: "让 Agent 学会拆解复杂任务",
        objective: "让小龙虾规划并执行一个多步骤任务",
        sortOrder: 4,
        status: "locked",
        stars: 0,
        steps: [
          {
            id: 7030,
            chapterId: 8004,
            title: "任务分解的艺术",
            contentType: "md",
            content: `# 任务分解的艺术

面对复杂任务时，Agent 不会试图"一步到位"，而是先制定计划。

### 为什么要分解任务？

- 降低每一步的难度
- 减少出错概率
- 便于中间结果验证
- 支持失败后局部重试

### 分解策略

**自顶向下分解**
\`\`\`
"建一个博客" →
  1. 设计数据结构
  2. 实现后端 API
  3. 搭建前端界面
  4. 部署上线
\`\`\`

**条件分支规划**
\`\`\`
"分析数据" →
  如果是 CSV → 用 pandas 读取
  如果是 JSON → 用 json 模块解析
  如果是数据库 → 用 SQL 查询
\`\`\``,
            sortOrder: 1,
          },
          {
            id: 7031,
            chapterId: 8004,
            title: "ReAct 范式",
            contentType: "md",
            content: `# ReAct：推理 + 行动

**ReAct**（Reasoning + Acting）是 Agent 最常用的工作范式：

\`\`\`
Thought: 我需要先查一下今天的天气
Action:  search("北京今天天气")
Observe: 北京今天晴，最高温度 25°C
Thought: 天气不错，适合户外活动，我来推荐几个地方
Action:  search("北京户外活动推荐")
Observe: [推荐列表...]
Answer:  今天北京天气很好（25°C晴天），推荐去...
\`\`\`

每个循环包含：
1. **Thought** — 思考当前状态和下一步
2. **Action** — 执行一个具体动作
3. **Observe** — 观察动作的结果

这个循环会一直持续，直到 Agent 认为可以给出最终回答。`,
            sortOrder: 2,
          },
          {
            id: 7032,
            chapterId: 8004,
            title: "闯关挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾用 ReAct 范式，规划并执行「创建一个简单的待办事项 App」的任务，至少分 3 步。",
              initialMessage:
                "我已经掌握了 ReAct 推理模式！给我一个复杂任务吧 💪",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content:
                    "好的！让我用 ReAct 范式来规划这个任务。",
                  delay: 800,
                  actionStatus: "thinking",
                  actionLabel: "正在制定计划...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content: `**🧠 Thought：** 创建待办事项 App 需要前端 + 数据管理。我来分步执行。

**📋 任务计划：**
1. 定义数据结构
2. 实现增删改查逻辑
3. 构建用户界面

---

**Step 1/3 — 定义数据结构**

**🧠 Thought：** 先设计 Todo 的数据模型
**⚡ Action：** 编写数据类型定义

\`\`\`typescript
interface Todo {
  id: string
  title: string
  completed: boolean
  createdAt: Date
}
\`\`\`

**👀 Observe：** 数据结构定义完成 ✓`,
                  delay: 2000,
                  actionStatus: "executing",
                  actionLabel: "Step 1/3 — 定义数据结构",
                },
                {
                  id: "m3",
                  role: "assistant",
                  content: `**Step 2/3 — 实现核心逻辑**

**🧠 Thought：** 需要实现添加、删除、切换完成状态
**⚡ Action：** 编写业务逻辑

\`\`\`typescript
function addTodo(title: string): Todo { ... }
function deleteTodo(id: string): void { ... }
function toggleTodo(id: string): void { ... }
function getTodos(): Todo[] { ... }
\`\`\`

**👀 Observe：** CRUD 逻辑实现完成 ✓`,
                  delay: 2000,
                  actionStatus: "executing",
                  actionLabel: "Step 2/3 — 实现核心逻辑",
                },
                {
                  id: "m4",
                  role: "assistant",
                  content: `**Step 3/3 — 构建界面**

**🧠 Thought：** 需要输入框、列表和操作按钮
**⚡ Action：** 生成 React 组件

\`\`\`tsx
function TodoApp() {
  return (
    <div>
      <h1>📝 待办事项</h1>
      <input placeholder="添加新任务..." />
      <ul>
        {todos.map(todo => (
          <li key={todo.id}>
            <input type="checkbox" checked={todo.completed} />
            <span>{todo.title}</span>
            <button>删除</button>
          </li>
        ))}
      </ul>
    </div>
  )
}
\`\`\`

**👀 Observe：** UI 组件生成完成 ✓

---

🎯 **任务完成！** 按照 ReAct 范式，我把"创建待办事项 App"分解为了 3 个步骤，每步都经历了 Thought → Action → Observe 的循环。`,
                  delay: 2500,
                  actionStatus: "done",
                  actionLabel: "全部步骤执行完成",
                },
                {
                  id: "m5",
                  role: "judge",
                  content:
                    "✅ **闯关成功！**\n\n小龙虾完美展示了 ReAct 范式：将任务分解为 3 个步骤，每步都包含 Thought → Action → Observe 循环。规划清晰，执行到位！",
                  delay: 1500,
                },
              ],
            },
            sortOrder: 3,
          },
        ],
      },

      /* ── 关卡 5：记忆与上下文管理 ── */
      {
        id: 8005,
        courseId: 9001,
        title: "记忆与上下文管理",
        intro: "让 Agent 拥有持久记忆能力",
        objective: "让小龙虾展示短期记忆和长期记忆的使用",
        sortOrder: 5,
        status: "locked",
        stars: 0,
        steps: [
          {
            id: 7040,
            chapterId: 8005,
            title: "Agent 的记忆系统",
            contentType: "md",
            content: `# Agent 的记忆系统

人类有短期记忆和长期记忆，AI Agent 也一样。

### 🔄 短期记忆（Working Memory）
- 当前对话的上下文窗口
- 随着对话进行自动更新
- 受限于上下文长度（Token 限制）

### 💿 长期记忆（Long-term Memory）
- 跨会话持久化存储
- 需要主动保存和检索
- 常用实现方式：
  - 向量数据库（语义检索）
  - 键值存储（精确检索）
  - 文件系统（结构化存储）

### 🎯 为什么记忆重要？
- 避免用户重复提供信息
- 保持对话的连贯性
- 支持长期任务和个性化服务`,
            sortOrder: 1,
          },
          {
            id: 7041,
            chapterId: 8005,
            title: "RAG：检索增强生成",
            contentType: "md",
            content: `# RAG：检索增强生成

**RAG**（Retrieval-Augmented Generation）是 Agent 使用长期记忆的核心技术。

### 工作原理

\`\`\`
用户提问 → 检索相关文档 → 将文档 + 提问一起发给 LLM → 生成回答
\`\`\`

### 优势
- ✅ 突破知识截止日期限制
- ✅ 回答基于真实数据，减少幻觉
- ✅ 可以引用来源
- ✅ 数据更新不需要重新训练模型

### 典型应用场景
- 企业知识库问答
- 文档智能助手
- 客服系统
- 代码库理解`,
            sortOrder: 2,
          },
          {
            id: 7042,
            chapterId: 8005,
            title: "闯关挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾先记住你的名字和职业，然后在后续回答中自然地使用这些信息。",
              initialMessage:
                "我的记忆模块已启动！你可以告诉我一些关于你的信息，我会记住的 🧠",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content:
                    "收到！让我把这些信息保存到记忆中...",
                  delay: 600,
                  actionStatus: "executing",
                  actionLabel: "正在写入长期记忆...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content:
                    "✅ 已保存到长期记忆：\n\n```json\n{\n  \"user_name\": \"用户\",\n  \"user_role\": \"AI 学习者\",\n  \"saved_at\": \"2026-03-25\"\n}\n```\n\n我已经记住了！现在你可以问我任何问题，我会结合你的信息来回答。",
                  delay: 1500,
                  actionStatus: "done",
                  actionLabel: "记忆写入完成",
                },
                {
                  id: "m3",
                  role: "assistant",
                  content:
                    "🔍 **检索记忆中...**\n\n根据我的记忆，你是一位 **AI 学习者**。基于你的背景，我建议你接下来可以关注 Agent 的实战应用——比如构建一个能自动帮你学习和整理笔记的 AI 助手。\n\n看，这就是记忆的力量！我不需要你重复自我介绍，因为你的信息已经在我的长期记忆里了 💾",
                  delay: 2000,
                  actionStatus: "done",
                  actionLabel: "记忆检索完成",
                },
                {
                  id: "m4",
                  role: "judge",
                  content:
                    "✅ **闯关成功！**\n\n小龙虾成功展示了记忆的写入和检索能力：先保存用户信息到长期记忆，然后在后续回答中自然地引用了这些信息。记忆系统运作正常！",
                  delay: 1500,
                },
              ],
            },
            sortOrder: 3,
          },
        ],
      },

      /* ── 关卡 6：实战 - 构建你的第一个 Agent ── */
      {
        id: 8006,
        courseId: 9001,
        title: "实战：构建你的第一个 Agent",
        intro: "综合运用所学知识，从零构建一个完整的 Agent",
        objective: "让小龙虾综合运用所有能力完成一个综合任务",
        sortOrder: 6,
        status: "locked",
        stars: 0,
        steps: [
          {
            id: 7050,
            chapterId: 8006,
            title: "Agent 架构总览",
            contentType: "md",
            content: `# 构建 Agent：架构总览

现在让我们把前面学到的所有知识整合起来！

一个完整的 Agent 系统架构如下：

\`\`\`
┌─────────────────────────────────────┐
│            用户输入层                │
├─────────────────────────────────────┤
│         Prompt 处理 & 路由          │
├──────────┬──────────┬───────────────┤
│ 记忆管理  │ 工具调用  │  推理规划     │
│ (RAG)    │ (Tools) │  (ReAct)     │
├──────────┴──────────┴───────────────┤
│           LLM 核心引擎              │
├─────────────────────────────────────┤
│           输出格式化层               │
└─────────────────────────────────────┘
\`\`\`

每一层都是你在前面关卡中学到的核心能力！`,
            sortOrder: 1,
          },
          {
            id: 7051,
            chapterId: 8006,
            title: "开始构建",
            contentType: "md",
            content: `# 开始构建你的 Agent

在这个终极挑战中，你需要引导小龙虾完成一个综合任务：

**任务场景：** 帮助用户调研一个技术话题并生成一份简要报告

Agent 需要综合运用：
- 📝 **Prompt 理解** — 解析用户需求
- 🔧 **工具调用** — 搜索信息、执行代码
- 🧠 **推理规划** — 制定调研计划
- 💾 **记忆管理** — 整合多步结果

> 🎯 这就是你在实际工作中使用 Agent 的真实场景。准备好了就进入挑战吧！`,
            sortOrder: 2,
          },
          {
            id: 7052,
            chapterId: 8006,
            title: "终极挑战",
            contentType: "challenge",
            content: "",
            challengeScript: {
              objective:
                "让小龙虾调研「2026年 AI Agent 框架」这个话题，搜索信息、整理对比并输出一份简要报告。",
              initialMessage:
                "所有模块已就绪！我可以搜索、编程、推理、记忆——全力以赴 🦞✨ 请给我下达任务！",
              messages: [
                {
                  id: "m1",
                  role: "assistant",
                  content:
                    "收到！这是一个综合任务，让我先制定计划。",
                  delay: 800,
                  actionStatus: "thinking",
                  actionLabel: "正在制定调研计划...",
                },
                {
                  id: "m2",
                  role: "assistant",
                  content: `**📋 调研计划（ReAct 模式）：**

1. 搜索当前主流 AI Agent 框架
2. 对比各框架的特点
3. 整理成报告

---

**Step 1/3 — 信息检索**

**⚡ Action：** 调用搜索工具
\`search("2026 AI Agent frameworks comparison")\``,
                  delay: 1500,
                  actionStatus: "executing",
                  actionLabel: "Step 1/3 — 正在搜索信息...",
                },
                {
                  id: "m3",
                  role: "assistant",
                  content: `**👀 Observe：** 搜索完成，获取到 5 个相关结果

**Step 2/3 — 信息整理**

**⚡ Action：** 对比分析各框架

| 框架 | 特点 | 适用场景 |
|------|------|---------|
| LangChain | 生态最丰富，社区活跃 | 通用 Agent 开发 |
| CrewAI | 多 Agent 协作 | 团队任务分工 |
| AutoGen | 微软出品，对话式 | 多轮对话场景 |
| Claude Agent SDK | 原生工具调用 | 生产级应用 |
| Dify | 低代码平台 | 快速原型验证 |`,
                  delay: 2500,
                  actionStatus: "executing",
                  actionLabel: "Step 2/3 — 正在整理对比...",
                },
                {
                  id: "m4",
                  role: "assistant",
                  content: `**Step 3/3 — 生成报告**

**⚡ Action：** 综合分析，生成报告

---

# 📊 2026 年 AI Agent 框架调研报告

## 概述
AI Agent 框架在 2026 年迎来爆发式发展，主流框架各有侧重。

## 框架推荐
- **快速入门：** Dify（零代码 → 低代码）
- **生产部署：** Claude Agent SDK（稳定、安全）
- **复杂协作：** CrewAI（多 Agent 编排）
- **灵活定制：** LangChain（生态丰富）

## 趋势
1. 多 Agent 协作成为主流
2. 安全性和可控性越来越受重视
3. 低代码 Agent 平台降低门槛
4. 工具调用标准趋向统一

---

💾 报告已保存到记忆中，随时可以调取！`,
                  delay: 3000,
                  actionStatus: "done",
                  actionLabel: "报告生成完成",
                },
                {
                  id: "m5",
                  role: "judge",
                  content:
                    "✅ **终极闯关成功！** 🎉🎉🎉\n\n小龙虾完美展示了一个 Agent 的全部能力：\n- ✅ 理解任务需求\n- ✅ 制定调研计划（推理规划）\n- ✅ 调用搜索工具（工具调用）\n- ✅ 整理对比分析（多步推理）\n- ✅ 保存到记忆（记忆管理）\n\n恭喜你完成了所有关卡！你已经掌握了 AI Agent 的核心知识 🏆",
                  delay: 2000,
                },
              ],
            },
            sortOrder: 3,
          },
        ],
      },
    ],
  },
]

/** 根据课程 ID 获取游戏课程 */
export function getGameCourse(courseId: number): GameCourseItem | undefined {
  return GAME_COURSES.find((c) => c.id === courseId)
}

/** 获取所有游戏课程（用于列表页混合展示） */
export function getGameCourseList(): GameCourseItem[] {
  return GAME_COURSES
}
